/* =====================================================================================
   Congress: the calendar, the deadlines, the abstracts and their outcomes.
   The AI lookup researches a named congress with live web search and returns facts WITH
   SOURCES; nothing is saved until a person has looked at them. Files ride the attachments
   machinery; deadlines ride the 0102 morning sweep.
   ===================================================================================== */

import { BACKEND } from './db';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

export interface Congress {
  id: string; name: string; short_name: string | null; edition: string | null;
  starts_on: string | null; ends_on: string | null; city: string | null;
  country: string | null; venue: string | null; url: string | null;
  abstract_url: string | null; abstract_deadline: string | null;
  early_reg_deadline: string | null; expected_attendance: number | null;
  our_intent: string | null; priority: string | null; notes: string | null;
}

export interface Abstract {
  id: string; congress_id: string; title: string; authors: string | null;
  presenter: string | null; abstract_ref: string | null; submitted_on: string | null;
  decision_on: string | null; outcome: string; notes: string | null;
}

export interface Lookup {
  name: string; short_name: string | null; edition: string | null;
  starts_on: string | null; ends_on: string | null; city: string | null;
  country_iso3: string | null; venue: string | null; url: string | null;
  abstract_url: string | null; abstract_deadline: string | null;
  early_reg_deadline: string | null; expected_attendance: number | null;
  confidence_notes: string; sources: { title: string; url: string }[];
}

export const OUTCOMES: [string, string][] = [
  ['drafting', 'Drafting'],
  ['submitted', 'Submitted'],
  ['under_review', 'Under review'],
  ['accepted_poster', 'Accepted: poster'],
  ['accepted_mini_oral', 'Accepted: mini oral'],
  ['accepted_oral', 'Accepted: oral'],
  ['rejected', 'Rejected'],
  ['withdrawn', 'Withdrawn'],
];

export async function fetchCongresses(): Promise<Congress[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('atlas').from('congresses')
    .select('*').order('starts_on', { ascending: true, nullsFirst: false });
  if (error) throw new Error(explain(error.message, 'read the congress calendar'));
  return (data ?? []) as Congress[];
}

export async function lookupCongress(query: string, existing?: Partial<Congress>): Promise<Lookup> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().functions.invoke('congress-lookup',
    { body: { query, existing } });
  if (error) {
    const ctx = (error as { context?: Response }).context;
    if (ctx) { try { const j = await ctx.json(); if (j?.error) throw new Error(j.error); } catch (e) {
      if ((e as Error).message) throw e; } }
    throw new Error(error.message);
  }
  if (data?.error) throw new Error(data.error);
  return data.congress as Lookup;
}

export async function saveCongress(c: Partial<Congress> & { id?: string }): Promise<string> {
  const { supabase } = await import('./supabase');
  const row = { ...c }; delete (row as { id?: string }).id;
  if (c.id) {
    const { error } = await supabase().schema('atlas').from('congresses').update(row).eq('id', c.id);
    if (error) throw new Error(explain(error.message, 'save the congress'));
    return c.id;
  }
  const { data, error } = await supabase().schema('atlas').from('congresses')
    .insert(row).select('id').single();
  if (error) throw new Error(explain(error.message, 'save the congress'));
  return data.id as string;
}

export async function fetchAbstracts(congressId?: string): Promise<Abstract[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  let q = supabase().schema('atlas').from('congress_abstracts')
    .select('*').order('created_at', { ascending: false });
  if (congressId) q = q.eq('congress_id', congressId);
  const { data, error } = await q;
  if (error) throw new Error(explain(error.message, 'read the abstracts'));
  return (data ?? []) as Abstract[];
}

export async function addAbstract(a: { congressId: string; title: string; authors?: string;
  presenter?: string; abstractRef?: string; submittedOn?: string; outcome?: string }): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data: me } = await supabase().schema('app').rpc('current_member_id');
  const { data, error } = await supabase().schema('atlas').from('congress_abstracts').insert({
    congress_id: a.congressId, title: a.title, authors: a.authors || null,
    presenter: a.presenter || null, abstract_ref: a.abstractRef || null,
    submitted_on: a.submittedOn || null,
    outcome: a.outcome ?? (a.submittedOn ? 'submitted' : 'drafting'),
    created_by: me ?? null,
  }).select('id').single();
  if (error) throw new Error(explain(error.message, 'add the abstract'));
  return data.id as string;
}

export async function updateAbstract(id: string, patch: Partial<Abstract>) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('atlas').from('congress_abstracts')
    .update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the abstract'));
}

/* ------------------------------------------------------------------ conference reports */

export interface Report {
  id: string; congress_id: string; title: string; attended_by: string | null;
  report_date: string; summary: string | null; body: string; status: string;
}

export async function fetchReports(congressId?: string): Promise<Report[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  let q = supabase().schema('atlas').from('congress_reports')
    .select('*').order('report_date', { ascending: false });
  if (congressId) q = q.eq('congress_id', congressId);
  const { data, error } = await q;
  if (error) throw new Error(explain(error.message, 'read the conference reports'));
  return (data ?? []) as Report[];
}

export async function saveReport(r: Partial<Report> & { id?: string }): Promise<string> {
  const { supabase } = await import('./supabase');
  const row: Record<string, unknown> = { ...r };
  delete row.id;
  if (r.id) {
    const { error } = await supabase().schema('atlas').from('congress_reports')
      .update(row).eq('id', r.id);
    if (error) throw new Error(explain(error.message, 'save the report'));
    return r.id;
  }
  const { data: me } = await supabase().schema('app').rpc('current_member_id');
  const { data, error } = await supabase().schema('atlas').from('congress_reports')
    .insert({ ...row, created_by: me ?? null }).select('id').single();
  if (error) throw new Error(explain(error.message, 'save the report'));
  return data.id as string;
}
